import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowIosDownward, CheckMark } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import AutocompleteSearch from '@/components/AutocompleteSearch';
import { PopperOverride } from '@/components/PopperOverride';
import { useOrgRoleDetailsQuery } from '@/modules/corporate/graphql/queries/generated/orgRoleDetails';
import { OrgRoleListItemFragment } from '@/modules/org-hierarchy/modules/org-roles/graphql/fragments/generated/OrgRoleListItem';
import useRoles from '@/modules/org-hierarchy/modules/org-roles/hooks/useRoles';
import { OrgRoleTypeEnum } from '@/types/schema';

import { autocompleteClasses } from '../../../styles';
import Loader from '../../Loader';
import { filterValueStyle } from '../styles';
import { FilterValueProps } from '../types';

const TYPE = [OrgRoleTypeEnum.Designation];

const renderRoles = (selectedRoleId?: string | null) => (role: OrgRoleListItemFragment) => {
  const isSelected = selectedRoleId === role.id;

  return (
    <Stack alignItems="center" className="mb-2 w-full py-10 px-12" justifyContent="between">
      <Typography
        className="truncate"
        color={isSelected ? 'text-text-primary' : 'text-text-secondary'}
        variant="body-short-02">
        {role.name}
      </Typography>
      {isSelected ? <CheckMark className="h-20 w-20 ms-12" fillColorToken="icon-active" /> : null}
    </Stack>
  );
};

// todo make autocomplete with JSON input to avoid repeatation of code
function OrgRoleFilter(props: FilterValueProps) {
  const { t } = useTranslation();
  const [searchStr, setSearchStr] = useState('');
  const { filterValue, onFilterValueChange, disabled, error } = props;
  const { roles, isLoading, fetchMore } = useRoles({ searchText: searchStr, type: TYPE });
  const [{ data: selectedRole, fetching: fetchingSelected }] = useOrgRoleDetailsQuery({
    variables: {
      id: filterValue!,
    },
    pause: !filterValue,
  });
  const onSelect = useCallback(
    (roleId?: string) => {
      onFilterValueChange(roleId);
    },
    [onFilterValueChange],
  );
  const hasError = useMemo(
    () => error && !disabled && !filterValue,
    [disabled, error, filterValue],
  );

  if (isLoading || fetchingSelected) {
    return <Loader className={filterValueStyle} />;
  }

  return (
    <Stack
      className={cn(filterValueStyle, {
        'cursor-not-allowed': disabled,
      })}
      direction="col">
      <AutocompleteSearch
        disableClearable
        PopperComponent={(val) => <PopperOverride placement="bottom-end" {...val} />}
        className="flex-1"
        classes={autocompleteClasses}
        closeIcon={null}
        data={roles}
        data-id="tag-and-properties"
        disabled={disabled}
        error={hasError}
        fetchMore={fetchMore}
        initialValue={selectedRole?.orgRole?.name}
        placeholder={t('Group:Editor.Value')}
        popupIcon={<ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />}
        renderers={{
          renderOption: renderRoles(filterValue),
        }}
        onChange={onSelect}
        onInputChange={setSearchStr}
      />
      {hasError ? (
        <Typography className="mt-8" color="text-text-error" variant="body-short-03">
          {t('Common:Form.Error.ValueCantBeEmpty')}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default OrgRoleFilter;
